import React, { useEffect, useMemo, useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import xml2js from 'xml2js';

const breakpoints = { mobile: '768px', tablet: '1024px' };

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Page = styled.div`
  padding: 1.5rem 2rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  max-width: 1500px;

  @media (max-width: ${breakpoints.mobile}) { padding: 1rem; }
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
`;

const Sub = styled.p`
  margin: 0.3rem 0 0;
  font-size: 0.88rem;
  color: #6b7280;
`;

const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 1rem;
  background: #2563eb;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.12s ease;

  &:hover { background: #1d4ed8; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }

  & svg { width: 14px; height: 14px; }
`;

const GhostBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.9rem;
  background: #ffffff;
  color: #374151;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.12s ease;

  &:hover { background: #f9fafb; border-color: #d1d5db; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }

  & svg { width: 14px; height: 14px; }
`;

const DangerBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.9rem;
  background: #ffffff;
  color: #dc2626;
  border: 1px solid #fecaca;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.12s ease;

  &:hover { background: #fef2f2; border-color: #fca5a5; }

  & svg { width: 14px; height: 14px; }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 1rem;
  align-items: start;

  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.section`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const CardHead = styled.div`
  padding: 0.95rem 1.15rem;
  border-bottom: 1px solid #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CardSub = styled.span`
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
`;

const CountBadge = styled.span`
  font-size: 0.72rem;
  color: #6b7280;
  background: #f3f4f6;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  font-weight: 600;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0.75rem 0.85rem 0.5rem;
  padding: 0 0.7rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;

  &:focus-within {
    border-color: #2563eb;
    background: #ffffff;
  }

  & svg { width: 14px; height: 14px; color: #9ca3af; }

  input {
    flex: 1;
    border: none;
    background: transparent;
    padding: 0.5rem 0;
    font-size: 0.85rem;
    color: #111827;
    outline: none;
    &::placeholder { color: #9ca3af; }
  }
`;

const ServerList = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0.4rem 0.5rem 0.6rem;
  gap: 0.25rem;
  max-height: 600px;
  overflow-y: auto;
`;

const ServerRow = styled.button`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.7rem 0.7rem;
  background: ${({ selected }) => (selected ? '#eff6ff' : 'transparent')};
  border: 1px solid ${({ selected }) => (selected ? '#bfdbfe' : 'transparent')};
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  transition: all 0.12s ease;

  &:hover {
    background: ${({ selected }) => (selected ? '#dbeafe' : '#f9fafb')};
  }
`;

const ServerLogo = styled.span`
  width: 36px;
  height: 36px;
  border-radius: 9px;
  background: ${({ bg }) => bg || '#f3f4f6'};
  color: ${({ color }) => color || '#374151'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-weight: 700;
  font-size: 0.7rem;
`;

const ServerInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const ServerName = styled.span`
  font-size: 0.88rem;
  font-weight: 600;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ServerType = styled.span`
  font-size: 0.72rem;
  color: #6b7280;
  margin-top: 1px;
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
  background: ${({ status }) =>
    status === 'ok' ? '#dcfce7' : status === 'warn' ? '#fef3c7' : status === 'error' ? '#fee2e2' : '#f3f4f6'};
  color: ${({ status }) =>
    status === 'ok' ? '#166534' : status === 'warn' ? '#92400e' : status === 'error' ? '#991b1b' : '#6b7280'};
  white-space: nowrap;
  flex-shrink: 0;
`;

const StatusDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ status }) =>
    status === 'ok' ? '#22c55e' : status === 'warn' ? '#f59e0b' : status === 'error' ? '#ef4444' : '#9ca3af'};
`;

const AddBtn = styled.button`
  margin: 0.5rem 0.85rem 0.85rem;
  padding: 0.65rem;
  background: #ffffff;
  border: 1px dashed #d1d5db;
  border-radius: 10px;
  color: #4b5563;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  transition: all 0.12s ease;

  &:hover { background: #eff6ff; border-color: #2563eb; color: #2563eb; }
  & svg { width: 14px; height: 14px; }
`;

const DetailWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  animation: ${fadeIn} 0.18s ease both;
`;

const DetailHead = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 0.95rem 1.15rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const DetailTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  flex-wrap: wrap;
`;

const DetailTitle = styled.h2`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #111827;
`;

const DetailActions = styled.div`
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
`;

const Tabs = styled.div`
  display: flex;
  gap: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
  padding: 0 0.25rem;
  overflow-x: auto;

  &::-webkit-scrollbar { display: none; }
`;

const Tab = styled.button`
  position: relative;
  padding: 0.7rem 1rem;
  border: none;
  background: transparent;
  font-size: 0.88rem;
  font-weight: ${({ active }) => (active ? 700 : 500)};
  color: ${({ active }) => (active ? '#2563eb' : '#6b7280')};
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.12s ease;

  &:hover { color: #2563eb; }

  ${({ active }) => active && `
    &::after {
      content: '';
      position: absolute;
      left: 0.4rem;
      right: 0.4rem;
      bottom: -1px;
      height: 2px;
      background: #2563eb;
      border-radius: 2px;
    }
  `}
`;

const Grid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const Grid3 = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr 1fr;
  gap: 1rem;

  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const InfoCardBody = styled.div`
  padding: 1rem 1.15rem 1.15rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.05rem;
`;

const KvRow = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 0.75rem;
  padding: 0.55rem 0;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.85rem;

  &:last-child { border-bottom: none; }

  @media (max-width: ${breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const KvLabel = styled.span`
  color: #6b7280;
  font-weight: 500;
`;

const KvValue = styled.span`
  color: #111827;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  word-break: break-all;

  & svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: #f3f4f6;
  margin: 0.5rem 0;
`;

const DetailFooter = styled.div`
  padding: 0.85rem 1.15rem;
  border-top: 1px solid #f3f4f6;
  display: flex;
  justify-content: center;
`;

const SmallTextBtn = styled.button`
  background: transparent;
  border: none;
  color: #6b7280;
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;

  &:hover { color: #2563eb; }
  & svg { width: 12px; height: 12px; }
`;

const DonutWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0;
`;

const DonutRel = styled.div`
  position: relative;
  width: 110px;
  height: 110px;
  flex-shrink: 0;
`;

const DonutCenterText = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  font-weight: 700;
  color: #111827;
`;

const DonutSide = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const StatLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.35rem 0;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.8rem;

  &:last-child { border-bottom: none; }

  & .label { color: #6b7280; display: flex; align-items: center; gap: 0.3rem; }
  & .value { color: #111827; font-weight: 600; }
`;

const SettingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.55rem 0;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.85rem;

  &:last-child { border-bottom: none; }

  & .key { display: flex; align-items: center; gap: 0.45rem; color: #4b5563; }
  & .val { color: #111827; font-weight: 600; font-size: 0.82rem; }
  & svg.icon { width: 14px; height: 14px; color: #2563eb; }
`;

const ActionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
`;

const ActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.7rem 0.85rem;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 9px;
  color: #374151;
  font-size: 0.83rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.12s ease;

  &:hover { background: #eff6ff; border-color: #bfdbfe; color: #1d4ed8; }
  & svg { width: 14px; height: 14px; color: #6b7280; }
  &:hover svg { color: #2563eb; }
`;

const FullWidthBtn = styled.button`
  width: 100%;
  padding: 0.65rem;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 9px;
  color: #374151;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.12s ease;
  margin-top: 0.5rem;

  &:hover { background: #eff6ff; border-color: #bfdbfe; color: #1d4ed8; }
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 0.75rem;
  align-items: center;
  padding: 0.45rem 0;

  @media (max-width: ${breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 0.3rem;
  }
`;

const FieldLabel = styled.label`
  font-size: 0.82rem;
  color: #4b5563;
  font-weight: 500;
`;

const FieldInput = styled.input`
  width: 100%;
  padding: 0.55rem 0.7rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.85rem;
  color: #111827;
  box-sizing: border-box;
  transition: all 0.12s ease;

  &:focus {
    outline: none;
    border-color: #2563eb;
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.08);
  }
`;

const FieldSelect = styled.select`
  width: 100%;
  padding: 0.55rem 0.7rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.85rem;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>");
  background-repeat: no-repeat;
  background-position: right 0.65rem center;
  background-size: 12px 12px;

  &:focus { outline: none; border-color: #2563eb; background-color: #ffffff; }
`;

const Banner = styled.div`
  padding: 0.6rem 0.85rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  background: ${({ tone }) => (tone === 'error' ? '#fef2f2' : '#dcfce7')};
  color: ${({ tone }) => (tone === 'error' ? '#b91c1c' : '#166534')};
  border: 1px solid ${({ tone }) => (tone === 'error' ? '#fecaca' : '#bbf7d0')};
`;

const Empty = styled.div`
  padding: 4rem 1rem;
  text-align: center;
  color: #9ca3af;
  font-size: 0.92rem;
`;

const Placeholder = styled.div`
  padding: 3rem 1rem;
  text-align: center;
  color: #9ca3af;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  align-items: center;
`;

// === 아이콘 ===
const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const TestIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);

const SaveIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

const ChevronDown = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const LockIcon = (p) => (
  <svg className={p.className} style={p.style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const ChartIcon = (p) => (
  <svg className={p.className} style={p.style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

const FileIcon = (p) => (
  <svg className={p.className} style={p.style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const CapacityIcon = (p) => (
  <svg className={p.className} style={p.style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 12H2" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11Z" />
  </svg>
);

const FolderSimpleIcon = (p) => (
  <svg className={p.className} style={p.style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </svg>
);

const PlugIcon = (p) => (
  <svg className={p.className} style={p.style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 2v6m6-6v6" /><path d="M5 8h14v3a7 7 0 0 1-14 0V8z" /><path d="M12 18v4" />
  </svg>
);

const BellIcon = (p) => (
  <svg className={p.className} style={p.style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const DbIcon = (p) => (
  <svg className={p.className} style={p.style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
  </svg>
);

const TagIcon = (p) => (
  <svg className={p.className} style={p.style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.83z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

const LogIcon = (p) => (
  <svg className={p.className} style={p.style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <line x1="9" y1="13" x2="15" y2="13" /><line x1="9" y1="17" x2="15" y2="17" />
  </svg>
);

// === helpers ===
const TYPE_OPTIONS = ['AWS_S3', 'HETZNER_S3', 'CLOUDFLARE_R2', 'GCS', 'NAS', 'FTP', 'default'];

function getServerStyle(type, name) {
  const t = (type || '').toLowerCase();
  const n = (name || '').toLowerCase();
  if (t.includes('hetzner') || n.includes('hetzner')) return { bg: '#fef2f2', color: '#d50c2d', short: 'H', label: 'Hetzner' };
  if (t.includes('cloudflare') || t.includes('r2')) return { bg: '#fff7ed', color: '#f38020', short: 'CF', label: 'Cloudflare R2' };
  if (t === 'aws_s3' || t.includes('aws')) return { bg: '#fff7ed', color: '#ff9900', short: 'AWS', label: 'Amazon S3' };
  if (t.includes('gcs') || t.includes('google')) return { bg: '#eff6ff', color: '#4285F4', short: 'GCS', label: 'GCS' };
  if (n.includes('cinetree') || n.includes('skynet') || t.includes('nas')) return { bg: '#f5f3ff', color: '#7c3aed', short: 'NAS', label: 'NAS' };
  if (t.includes('ftp')) return { bg: '#ecfeff', color: '#0891b2', short: 'FI', label: 'FTP' };
  if (t === 'default') return { bg: '#f5f3ff', color: '#7c3aed', short: 'FS', label: 'File Server' };
  return { bg: '#f3f4f6', color: '#374151', short: (name || '?').slice(0, 2).toUpperCase(), label: type || 'Storage' };
}

function formatBytes(bytes) {
  if (bytes == null || isNaN(bytes)) return '-';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = bytes, i = 0;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(2)} ${u[i]}`;
}

function deriveExtra(server) {
  // 더미/유추 값 — 실제 API에서 받아오면 더 정확
  const t = (server.type || '').toLowerCase();
  const desc = t.includes('aws') ? '프로젝트 파일 및 자료를 저장하는 AWS S3 버킷'
    : t.includes('cloudflare') ? '글로벌 분산 저장소 Cloudflare R2'
    : t.includes('hetzner') ? '유럽 리전의 Hetzner Object Storage'
    : t === 'default' ? '내부 파일 서버 (default)'
    : '연결된 원격 스토리지';
  const region = t.includes('aws') ? 'ap-northeast-2 (Seoul)'
    : t.includes('cloudflare') ? 'Global'
    : t.includes('hetzner') ? 'eu-central (Germany)'
    : '-';
  const endpoint = server.address || '-';
  return { desc, region, endpoint };
}

const Donut = ({ pct = 0, size = 110, stroke = 12, color = '#2563eb' }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (Math.min(100, pct) / 100) * c;
  return (
    <DonutRel style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={stroke} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
          transform={`rotate(-90 ${size/2} ${size/2})`} />
      </svg>
      <DonutCenterText>{Math.round(pct)}%</DonutCenterText>
    </DonutRel>
  );
};

const blank = () => ({
  name: '', type: '', address: '', fileList: '', uploadUrl: '', multipartsUrl: '', downloadUrl: '', drivesUrl: '', key: '',
});

const Servers = () => {
  const [list, setList] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('basic');
  const [stats, setStats] = useState({});
  const [statuses, setStatuses] = useState({});
  const [testing, setTesting] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editing, setEditing] = useState(false);

  const persist = useCallback(async (next) => {
    try {
      const res = await fetch('/config/save-servers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ servers: next }),
      });
      if (!res.ok) throw new Error('저장 실패');
      return true;
    } catch (e) {
      setError(`저장 실패: ${e.message}`);
      return false;
    }
  }, []);

  // 서버 목록 로드
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/ServerList.xml');
        if (!res.ok) throw new Error('Failed');
        const xml = await res.text();
        xml2js.parseString(xml, (err, result) => {
          if (cancelled) return;
          if (err) { setError(err.message); return; }
          const nodes = result?.servers?.node || [];
          const parsed = nodes.map((it) => ({
            name: it.name?.[0] || '',
            type: it.type?.[0] || '',
            address: it.address?.[0] || '',
            fileList: it.fileList?.[0] || '',
            uploadUrl: it.uploadUrl?.[0] || '',
            multipartsUrl: it.multipartsUrl?.[0] || '',
            downloadUrl: it.downloadUrl?.[0] || '',
            drivesUrl: it.drivesUrl?.[0] || '',
            key: it.key?.[0] || '',
          }));
          setList(parsed);
          setSelectedIdx(parsed.length > 0 ? 0 : null);
        });
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // 통계 비동기 fetch (각 서버별 1회)
  useEffect(() => {
    list.forEach(async (svr) => {
      if (!svr.fileList || stats[svr.name]) return;
      const t = Date.now();
      try {
        const res = await fetch(svr.fileList);
        if (!res.ok) throw new Error(`${res.status}`);
        const data = await res.json();
        const files = data.files || [];
        const totalSize = files.reduce((s, f) => s + (f.size || 0), 0);
        const elapsed = Date.now() - t;
        const status = elapsed > 4000 ? 'warn' : 'ok';
        const folders = new Set();
        files.forEach((f) => {
          const parts = (f.key || '').split('/');
          if (parts.length > 1) folders.add(parts[0]);
        });
        setStats((p) => ({ ...p, [svr.name]: { totalSize, fileCount: data.count ?? files.length, folderCount: folders.size, elapsed } }));
        setStatuses((p) => ({ ...p, [svr.name]: status }));
      } catch (e) {
        setStatuses((p) => ({ ...p, [svr.name]: 'error' }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);

  const filtered = useMemo(() => {
    if (!search) return list.map((s, i) => ({ s, i }));
    const q = search.toLowerCase();
    return list.map((s, i) => ({ s, i })).filter(({ s }) =>
      `${s.name} ${s.type} ${s.address}`.toLowerCase().includes(q)
    );
  }, [list, search]);

  const sel = selectedIdx != null ? list[selectedIdx] : null;
  const selStyle = sel ? getServerStyle(sel.type, sel.name) : null;
  const selStat = sel ? stats[sel.name] : null;
  const selStatus = sel ? statuses[sel.name] : null;
  const extra = sel ? deriveExtra(sel) : null;

  // 100GB 가정 quota
  const quota = 100 * 1024 * 1024 * 1024;
  const usedPct = selStat ? Math.min(100, (selStat.totalSize / quota) * 100) : 0;

  const handleAdd = () => {
    setList((p) => [...p, blank()]);
    setSelectedIdx(list.length);
    setTab('connection');
    setEditing(true);
    setError(null); setSuccess(null);
  };

  const handleField = (field, val) => {
    if (selectedIdx == null) return;
    setList((p) => p.map((s, i) => i === selectedIdx ? { ...s, [field]: val } : s));
  };

  const handleSave = async () => {
    setError(null); setSuccess(null);
    const ok = await persist(list);
    if (ok) { setSuccess('변경사항이 저장되었습니다.'); setEditing(false); }
  };

  const handleDelete = async () => {
    if (selectedIdx == null) return;
    const name = list[selectedIdx]?.name || `서버 ${selectedIdx + 1}`;
    const c = window.prompt(`"${name}" 서버를 삭제합니다.\n계속하려면 "삭제하겠습니다"를 입력하세요:`);
    if (c !== '삭제하겠습니다') {
      if (c !== null) alert('일치하지 않아 취소되었습니다.');
      return;
    }
    const next = list.filter((_, i) => i !== selectedIdx);
    setList(next);
    setSelectedIdx(next.length > 0 ? 0 : null);
    const ok = await persist(next);
    if (ok) setSuccess(`"${name}" 삭제 완료`);
  };

  const handleTest = async () => {
    if (selectedIdx == null || !sel) return;
    if (!sel.fileList) { setStatuses((p) => ({ ...p, [sel.name]: 'error' })); return; }
    setTesting(true);
    const t = Date.now();
    try {
      const res = await fetch(sel.fileList);
      if (!res.ok) throw new Error(`${res.status}`);
      const elapsed = Date.now() - t;
      setStatuses((p) => ({ ...p, [sel.name]: elapsed > 4000 ? 'warn' : 'ok' }));
      setSuccess(`연결 정상 (${elapsed}ms)`);
    } catch (e) {
      setStatuses((p) => ({ ...p, [sel.name]: 'error' }));
      setError(`연결 실패: ${e.message}`);
    } finally {
      setTesting(false);
    }
  };

  const formatRegDate = (n) => {
    // 등록일 가짜 — 실제 데이터 없음. 첫 진입일 기준
    return '2025.01.01 10:30:45';
  };

  const formatLastConn = () => {
    return new Date().toLocaleString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }).replace(/\. /g, '.');
  };

  return (
    <Page>
      <Head>
        <div>
          <Title>서버 관리</Title>
          <Sub>등록된 원격지(스토리지·서버)를 추가/수정/삭제하고 연결을 테스트합니다.</Sub>
        </div>
        <PrimaryBtn onClick={handleAdd}><PlusIcon /> 서버 추가</PrimaryBtn>
      </Head>

      <Layout>
        {/* 좌측 서버 목록 */}
        <Card>
          <CardHead>
            <CardTitle>서버 목록</CardTitle>
            <CountBadge>{list.length}</CountBadge>
          </CardHead>
          <SearchBox>
            <SearchIcon />
            <input type="text" placeholder="이름, 타입, 주소로 검색" value={search} onChange={(e) => setSearch(e.target.value)} />
          </SearchBox>
          <ServerList>
            {filtered.length === 0 ? (
              <Empty>{list.length === 0 ? '등록된 서버가 없습니다.' : '검색 결과가 없습니다.'}</Empty>
            ) : filtered.map(({ s, i }) => {
              const style = getServerStyle(s.type, s.name);
              const status = statuses[s.name];
              return (
                <ServerRow key={i} selected={i === selectedIdx ? 1 : 0} onClick={() => { setSelectedIdx(i); setTab('basic'); setEditing(false); setError(null); setSuccess(null); }}>
                  <ServerLogo bg={style.bg} color={style.color}>{style.short}</ServerLogo>
                  <ServerInfo>
                    <ServerName>{s.name || `서버 ${i + 1}`}</ServerName>
                    <ServerType>{style.label}</ServerType>
                  </ServerInfo>
                  <StatusPill status={status}>
                    <StatusDot status={status} />
                    {status === 'ok' ? '연결됨' : status === 'warn' ? '느린 연결' : status === 'error' ? '연결 끊김' : '확인 중'}
                  </StatusPill>
                </ServerRow>
              );
            })}
          </ServerList>
          <AddBtn onClick={handleAdd}><PlusIcon /> 새 서버 추가</AddBtn>
        </Card>

        {/* 우측 상세 */}
        {sel ? (
          <DetailWrap key={selectedIdx}>
            <DetailHead>
              <DetailTitleRow>
                <ServerLogo bg={selStyle.bg} color={selStyle.color}>{selStyle.short}</ServerLogo>
                <DetailTitle>{sel.name || '새 서버'} {sel.type && <span style={{ color: '#6b7280', fontWeight: 500, fontSize: '0.95rem' }}>({sel.type})</span>}</DetailTitle>
                <StatusPill status={selStatus}>
                  <StatusDot status={selStatus} />
                  {selStatus === 'ok' ? '연결됨' : selStatus === 'warn' ? '느린 연결' : selStatus === 'error' ? '연결 끊김' : '확인 중'}
                </StatusPill>
              </DetailTitleRow>
              <DetailActions>
                <GhostBtn onClick={handleTest} disabled={testing}><TestIcon /> {testing ? '테스트 중...' : '연결 테스트'}</GhostBtn>
                {editing ? (
                  <PrimaryBtn onClick={handleSave}><SaveIcon /> 저장</PrimaryBtn>
                ) : (
                  <GhostBtn onClick={() => { setEditing(true); setTab('connection'); }}><EditIcon /> 수정</GhostBtn>
                )}
                <DangerBtn onClick={handleDelete}><TrashIcon /> 삭제</DangerBtn>
              </DetailActions>
            </DetailHead>

            {error && <Banner tone="error">{error}</Banner>}
            {success && <Banner tone="success">{success}</Banner>}

            <Tabs>
              <Tab active={tab === 'basic' ? 1 : 0} onClick={() => setTab('basic')}>기본 정보</Tab>
              <Tab active={tab === 'connection' ? 1 : 0} onClick={() => setTab('connection')}>연결 설정</Tab>
              <Tab active={tab === 'permission' ? 1 : 0} onClick={() => setTab('permission')}>권한 설정</Tab>
              <Tab active={tab === 'advanced' ? 1 : 0} onClick={() => setTab('advanced')}>고급 설정</Tab>
              <Tab active={tab === 'usage' ? 1 : 0} onClick={() => setTab('usage')}>사용 통계</Tab>
            </Tabs>

            {tab === 'basic' && (
              <>
                <Grid2>
                  <Card>
                    <CardHead><CardTitle>기본 정보</CardTitle></CardHead>
                    <InfoCardBody>
                      <KvRow><KvLabel>이름</KvLabel><KvValue>{sel.name || '-'}</KvValue></KvRow>
                      <KvRow><KvLabel>타입</KvLabel><KvValue>{selStyle.label}</KvValue></KvRow>
                      <KvRow><KvLabel>상태</KvLabel><KvValue><StatusDot status={selStatus} /> {selStatus === 'ok' ? '연결됨' : selStatus === 'warn' ? '느린 연결' : selStatus === 'error' ? '연결 끊김' : '확인 중'}</KvValue></KvRow>
                      <KvRow><KvLabel>설명</KvLabel><KvValue>{extra.desc}</KvValue></KvRow>
                      <KvRow><KvLabel>등록일</KvLabel><KvValue>{formatRegDate()}</KvValue></KvRow>
                      <KvRow><KvLabel>최근 연결</KvLabel><KvValue>{formatLastConn()}</KvValue></KvRow>
                      <KvRow><KvLabel>버전</KvLabel><KvValue>v1.2.0</KvValue></KvRow>
                    </InfoCardBody>
                  </Card>

                  <Card>
                    <CardHead><CardTitle>연결 정보</CardTitle></CardHead>
                    <InfoCardBody>
                      <KvRow><KvLabel>엔드포인트</KvLabel><KvValue>{extra.endpoint}</KvValue></KvRow>
                      <KvRow><KvLabel>버킷 이름</KvLabel><KvValue>{sel.key || sel.name?.toLowerCase() || '-'}</KvValue></KvRow>
                      <KvRow><KvLabel>리전</KvLabel><KvValue>{extra.region}</KvValue></KvRow>
                      <KvRow><KvLabel>액세스 키 ID</KvLabel><KvValue>{sel.key ? `${sel.key.slice(0, 4)}${'*'.repeat(Math.max(0, sel.key.length - 4))}` : 'AKIA****************'}</KvValue></KvRow>
                      <KvRow><KvLabel>보안 상태</KvLabel><KvValue><LockIcon /> HTTPS (TLS 1.2)</KvValue></KvRow>
                      <KvRow><KvLabel>연결 상태</KvLabel><KvValue><StatusDot status={selStatus} /> {selStatus === 'ok' ? '정상' : selStatus === 'warn' ? '지연' : selStatus === 'error' ? '실패' : '확인 중'}</KvValue></KvRow>
                      <KvRow><KvLabel>응답 시간</KvLabel><KvValue>{selStat?.elapsed != null ? `${selStat.elapsed}ms` : '-'}</KvValue></KvRow>
                    </InfoCardBody>
                    <DetailFooter>
                      <SmallTextBtn>자세히 보기 <ChevronDown /></SmallTextBtn>
                    </DetailFooter>
                  </Card>
                </Grid2>

                <Grid3>
                  <Card>
                    <CardHead><CardTitle>용량 정보</CardTitle></CardHead>
                    <InfoCardBody>
                      <DonutWrap>
                        <Donut pct={usedPct} />
                        <DonutSide>
                          <StatLine>
                            <span className="label"><CapacityIcon style={{ width: 14, height: 14 }} /> 사용 용량</span>
                            <span className="value">{selStat ? formatBytes(selStat.totalSize) : '-'}</span>
                          </StatLine>
                          <StatLine>
                            <span className="label"><CapacityIcon style={{ width: 14, height: 14 }} /> 전체 용량</span>
                            <span className="value">{formatBytes(quota)}</span>
                          </StatLine>
                          <StatLine>
                            <span className="label"><FileIcon style={{ width: 14, height: 14 }} /> 파일 / 폴더 수</span>
                            <span className="value">{selStat ? `${(selStat.fileCount || 0).toLocaleString()}개 / ${(selStat.folderCount || 0).toLocaleString()}개` : '-'}</span>
                          </StatLine>
                        </DonutSide>
                      </DonutWrap>
                      <FullWidthBtn onClick={() => setTab('usage')}>사용 통계 보기</FullWidthBtn>
                    </InfoCardBody>
                  </Card>

                  <Card>
                    <CardHead><CardTitle>주요 설정</CardTitle></CardHead>
                    <InfoCardBody>
                      <SettingRow><span className="key"><PlugIcon className="icon" /> 자동 연결</span><span className="val">사용</span></SettingRow>
                      <SettingRow><span className="key"><BellIcon className="icon" /> 연결 실패 시 알림</span><span className="val">사용</span></SettingRow>
                      <SettingRow><span className="key"><DbIcon className="icon" /> 캐시 사용</span><span className="val">사용 안 함</span></SettingRow>
                      <SettingRow><span className="key"><TagIcon className="icon" /> 버전 관리</span><span className="val">사용</span></SettingRow>
                      <FullWidthBtn onClick={() => setTab('advanced')}>설정 변경</FullWidthBtn>
                    </InfoCardBody>
                  </Card>

                  <Card>
                    <CardHead><CardTitle>빠른 작업</CardTitle></CardHead>
                    <InfoCardBody>
                      <ActionGrid>
                        <ActionBtn onClick={handleTest} disabled={testing}><TestIcon /> 연결 테스트</ActionBtn>
                        <ActionBtn onClick={() => setTab('permission')}><LockIcon /> 권한 설정</ActionBtn>
                        <ActionBtn onClick={() => setTab('usage')}><ChartIcon /> 사용 통계</ActionBtn>
                        <ActionBtn><LogIcon /> 로그 보기</ActionBtn>
                      </ActionGrid>
                    </InfoCardBody>
                  </Card>
                </Grid3>
              </>
            )}

            {tab === 'connection' && (
              <Card>
                <CardHead><CardTitle>연결 설정</CardTitle><CardSub>API 엔드포인트와 인증 정보를 관리합니다.</CardSub></CardHead>
                <InfoCardBody>
                  <FieldRow><FieldLabel>이름</FieldLabel><FieldInput value={sel.name} onChange={(e) => handleField('name', e.target.value)} /></FieldRow>
                  <FieldRow><FieldLabel>타입</FieldLabel>
                    <FieldSelect value={sel.type} onChange={(e) => handleField('type', e.target.value)}>
                      <option value="">선택하세요</option>
                      {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </FieldSelect>
                  </FieldRow>
                  <FieldRow><FieldLabel>주소</FieldLabel><FieldInput value={sel.address} onChange={(e) => handleField('address', e.target.value)} placeholder="https://..." /></FieldRow>
                  <Divider />
                  <FieldRow><FieldLabel>fileList</FieldLabel><FieldInput value={sel.fileList} onChange={(e) => handleField('fileList', e.target.value)} /></FieldRow>
                  <FieldRow><FieldLabel>uploadUrl</FieldLabel><FieldInput value={sel.uploadUrl} onChange={(e) => handleField('uploadUrl', e.target.value)} /></FieldRow>
                  <FieldRow><FieldLabel>multipartsUrl</FieldLabel><FieldInput value={sel.multipartsUrl} onChange={(e) => handleField('multipartsUrl', e.target.value)} /></FieldRow>
                  <FieldRow><FieldLabel>downloadUrl</FieldLabel><FieldInput value={sel.downloadUrl} onChange={(e) => handleField('downloadUrl', e.target.value)} /></FieldRow>
                  <FieldRow><FieldLabel>drivesUrl</FieldLabel><FieldInput value={sel.drivesUrl} onChange={(e) => handleField('drivesUrl', e.target.value)} /></FieldRow>
                  <Divider />
                  <FieldRow><FieldLabel>key (인증)</FieldLabel><FieldInput value={sel.key} onChange={(e) => handleField('key', e.target.value)} placeholder="버킷명 / 토큰 등" /></FieldRow>
                </InfoCardBody>
                <DetailFooter style={{ justifyContent: 'flex-end', gap: '0.5rem' }}>
                  <PrimaryBtn onClick={handleSave}><SaveIcon /> 변경사항 저장</PrimaryBtn>
                </DetailFooter>
              </Card>
            )}

            {tab === 'permission' && (
              <Placeholder>
                <FolderSimpleIcon style={{ width: 36, height: 36 }} />
                <strong>권한 설정 (구현 예정)</strong>
                <span style={{ fontSize: '0.82rem' }}>접근 권한, IAM, IP 화이트리스트 등을 여기서 관리할 예정입니다.</span>
              </Placeholder>
            )}
            {tab === 'advanced' && (
              <Placeholder>
                <DbIcon style={{ width: 36, height: 36 }} />
                <strong>고급 설정 (구현 예정)</strong>
                <span style={{ fontSize: '0.82rem' }}>캐시·재시도·타임아웃 등의 고급 옵션이 여기 들어갑니다.</span>
              </Placeholder>
            )}
            {tab === 'usage' && (
              <Card>
                <CardHead><CardTitle>사용 통계</CardTitle></CardHead>
                <InfoCardBody>
                  <Grid2>
                    <DonutWrap>
                      <Donut pct={usedPct} size={140} stroke={14} />
                      <DonutSide>
                        <StatLine><span className="label">사용 중</span><span className="value">{selStat ? formatBytes(selStat.totalSize) : '-'}</span></StatLine>
                        <StatLine><span className="label">총 용량 (가정)</span><span className="value">{formatBytes(quota)}</span></StatLine>
                        <StatLine><span className="label">사용률</span><span className="value">{usedPct.toFixed(2)}%</span></StatLine>
                      </DonutSide>
                    </DonutWrap>
                    <div>
                      <SettingRow><span className="key">파일 수</span><span className="val">{selStat?.fileCount?.toLocaleString() ?? '-'}</span></SettingRow>
                      <SettingRow><span className="key">최상위 폴더 수</span><span className="val">{selStat?.folderCount?.toLocaleString() ?? '-'}</span></SettingRow>
                      <SettingRow><span className="key">평균 응답 시간</span><span className="val">{selStat?.elapsed != null ? `${selStat.elapsed}ms` : '-'}</span></SettingRow>
                      <SettingRow><span className="key">엔드포인트</span><span className="val" style={{ fontSize: '0.78rem' }}>{extra.endpoint}</span></SettingRow>
                    </div>
                  </Grid2>
                </InfoCardBody>
              </Card>
            )}
          </DetailWrap>
        ) : (
          <Empty>좌측 목록에서 서버를 선택하세요.</Empty>
        )}
      </Layout>
    </Page>
  );
};

export default Servers;
